import { useIntl } from 'react-intl';

import { LoyaltyPromotionType } from 'generated/graphql-gateway';
import { useCart } from 'hooks/use-cart';
import { selectors, useAppSelector } from 'state/global-state';
import { IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import { getEvaluationResultMessage } from 'state/loyalty/hooks/utils/helpers';

import { IGetErrorMessagesProps } from './types';

export const useGetPaymentErrors = ({ cartPriceTooLow }: { cartPriceTooLow: boolean }) => {
  const formatters = useIntl();
  const incentiveEvaluationErrors = useAppSelector(state =>
    selectors.loyalty.selectIncentiveErrorsWithCode(
      state,
      IncentiveEvaluationErrorCodes.INVALID_PAYMENT_METHOD
    )
  );
  const { cartEntries } = useCart();

  // This method returns an array to handle other errors in the future
  // similar to `getErrorMessages`
  const getLoyaltyPaymentErrorMessages = () => {
    if (!incentiveEvaluationErrors.length) {
      return [];
    }

    return [
      {
        messageId: '',
        message: getEvaluationResultMessage({
          evaluationResult: incentiveEvaluationErrors[0],
          formatters,
          incentiveType: LoyaltyPromotionType.OFFER,
          cartEntries,
        }),
      },
    ];
  };

  const getErrorMessages = ({
    areDeliveryDetailsWithError,
    invalidAddressLine2,
    cartPriceTooLow: serverOrderCartPriceTooLow,
    checkoutPaymentMethodNeedRevaultError,
    invalidVehicleDescription,
    invalidOfferError,
    invalidPaymentMethodError,
    invalidReloadError,
    invalidTipAmountError,
    noDirectPayAndNoPrepaidMethodError,
    pricedAndAvailableError,
    storeIsClosing,
    unableToReloadCardError,
  }: IGetErrorMessagesProps) => {
    const paymentErrors = [
      { messageId: 'invalidVehicleDescription', hasError: invalidVehicleDescription },
      { messageId: 'invalidOfferError', hasError: invalidOfferError },
      { messageId: 'invalidPaymentMethodError', hasError: invalidPaymentMethodError },
      { messageId: 'invalidTipAmountError', hasError: invalidTipAmountError },
      {
        messageId: 'minimumPriceError',
        hasError: !!cartPriceTooLow || !!serverOrderCartPriceTooLow,
      },
      { messageId: 'unableToReloadCardError', hasError: unableToReloadCardError },
      { messageId: 'pricedAndAvailableError', hasError: pricedAndAvailableError },
      { messageId: 'areDeliveryDetailsValid', hasError: areDeliveryDetailsWithError },
      { messageId: 'invalidAddressLine2', hasError: invalidAddressLine2 },
      {
        messageId: 'noDirectPayAndNoPrepaidMethodError',
        hasError: noDirectPayAndNoPrepaidMethodError,
      },
      { messageId: 'invalidReloadError', hasError: invalidReloadError },
      {
        messageId: 'checkoutPaymentMethodNeedRevaultError',
        hasError: checkoutPaymentMethodNeedRevaultError,
      },
      { messageId: 'storeIsClosingError', hasError: storeIsClosing },
    ];
    const paymentErrorMessageIds = paymentErrors.flatMap(item =>
      item.hasError ? [{ ...item }] : []
    );
    return paymentErrorMessageIds;
  };

  return {
    getErrorMessages,
    getLoyaltyPaymentErrorMessages,
  };
};
